<template>
  <div class="suppliers-page">
    <header class="suppliers-page__header">
      <div>
        <h1>{{ $t('catalog.suppliers.title') }}</h1>
        <p>{{ $t('catalog.suppliers.subtitle') }}</p>
      </div>
      <button type="button" class="suppliers-page__btn" @click="openCreate">
        <i class="mdi mdi-plus"></i>
        {{ $t('catalog.suppliers.add') }}
      </button>
    </header>

    <div v-if="pagedSuppliers.length" class="suppliers-page__grid">
      <article v-for="supplier in pagedSuppliers" :key="supplier.id" class="supplier-card">
        <div class="supplier-card__top">
          <div class="supplier-card__icon">
            <i class="mdi mdi-truck-outline"></i>
          </div>
          <div class="supplier-card__actions">
            <button
              type="button"
              class="supplier-card__icon-btn"
              :title="$t('catalog.common.edit')"
              @click="openEdit(supplier)"
            >
              <i class="mdi mdi-pencil-outline"></i>
            </button>
            <button
              type="button"
              class="supplier-card__icon-btn supplier-card__icon-btn--danger"
              :title="$t('catalog.common.delete')"
              @click="askDelete(supplier)"
            >
              <i class="mdi mdi-trash-can-outline"></i>
            </button>
          </div>
        </div>
        <h3>{{ supplier.name }}</h3>
        <p v-if="supplier.phone" class="supplier-card__meta">{{ supplier.phone }}</p>
        <p v-if="supplier.note" class="supplier-card__note">{{ supplier.note }}</p>
      </article>
    </div>

    <div v-else class="suppliers-page__empty">
      <i class="mdi mdi-truck-plus-outline"></i>
      <p>{{ $t('catalog.suppliers.empty') }}</p>
      <span>{{ $t('catalog.suppliers.emptyHint') }}</span>
    </div>

    <div v-if="pageCount > 1" class="suppliers-page__pagination">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="5"
        color="#0b4f8a"
      />
    </div>

    <v-dialog v-model="formOpen" max-width="460" content-class="supplier-dialog-wrap">
      <div class="supplier-modal">
        <header class="supplier-modal__header">
          <div class="supplier-modal__badge">
            <i :class="['mdi', isEdit ? 'mdi-pencil-outline' : 'mdi-truck-plus-outline']"></i>
          </div>
          <div>
            <h2>{{ isEdit ? $t('catalog.suppliers.editTitle') : $t('catalog.suppliers.createTitle') }}</h2>
            <p>{{ isEdit ? $t('catalog.suppliers.editHint') : $t('catalog.suppliers.createHint') }}</p>
          </div>
          <button type="button" class="supplier-modal__close" @click="formOpen = false">
            <i class="mdi mdi-close"></i>
          </button>
        </header>

        <div class="supplier-modal__body">
          <label class="supplier-modal__field">
            <span>{{ $t('catalog.common.name') }}</span>
            <input
              v-model.trim="form.name"
              type="text"
              :placeholder="$t('catalog.suppliers.namePlaceholder')"
              maxlength="255"
              @keyup.enter="save"
            >
          </label>
          <label class="supplier-modal__field">
            <span>{{ $t('catalog.suppliers.phone') }}</span>
            <input
              v-model.trim="form.phone"
              type="tel"
              :placeholder="$t('catalog.suppliers.phonePlaceholder')"
              maxlength="40"
            >
          </label>
          <label class="supplier-modal__field">
            <span>{{ $t('catalog.suppliers.note') }}</span>
            <input
              v-model.trim="form.note"
              type="text"
              :placeholder="$t('catalog.suppliers.notePlaceholder')"
              maxlength="500"
            >
          </label>
        </div>

        <footer class="supplier-modal__footer">
          <button type="button" class="supplier-modal__btn supplier-modal__btn--ghost" @click="formOpen = false">
            {{ $t('catalog.common.cancel') }}
          </button>
          <button
            type="button"
            class="supplier-modal__btn"
            :disabled="saving || !form.name"
            @click="save"
          >
            <i v-if="saving" class="mdi mdi-loading mdi-spin"></i>
            <span>{{ saving ? $t('catalog.common.saving') : $t('catalog.common.save') }}</span>
          </button>
        </footer>
      </div>
    </v-dialog>

    <v-dialog v-model="deleteOpen" max-width="420" content-class="supplier-dialog-wrap">
      <div class="supplier-modal supplier-modal--danger">
        <header class="supplier-modal__header">
          <div class="supplier-modal__badge supplier-modal__badge--danger">
            <i class="mdi mdi-trash-can-outline"></i>
          </div>
          <div>
            <h2>{{ $t('catalog.suppliers.deleteTitle') }}</h2>
            <p>{{ $t('catalog.suppliers.deleteWarning') }}</p>
          </div>
          <button type="button" class="supplier-modal__close" @click="deleteOpen = false">
            <i class="mdi mdi-close"></i>
          </button>
        </header>

        <div class="supplier-modal__body">
          <p class="supplier-modal__warning">
            {{ $t('catalog.suppliers.deleteDescription', { name: deleteName }) }}
          </p>
        </div>

        <footer class="supplier-modal__footer">
          <button type="button" class="supplier-modal__btn supplier-modal__btn--ghost" @click="deleteOpen = false">
            {{ $t('catalog.common.cancel') }}
          </button>
          <button
            type="button"
            class="supplier-modal__btn supplier-modal__btn--danger"
            :disabled="deleting"
            @click="confirmDelete"
          >
            <i v-if="deleting" class="mdi mdi-loading mdi-spin"></i>
            <span>{{ deleting ? $t('catalog.common.deleting') : $t('catalog.common.delete') }}</span>
          </button>
        </footer>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { createSupplier, deleteSupplier, getSuppliers, updateSupplier } from '@/api/suppliers'
import { notify, notifyError } from '@/utils/notify'

const PER_PAGE = 12
const emptyForm = () => ({ name: '', phone: '', note: '' })

export default {
  name: 'SupplierControl',
  data() {
    return {
      suppliers: [],
      page: 1,
      formOpen: false,
      isEdit: false,
      supplierId: null,
      form: emptyForm(),
      saving: false,
      deleteOpen: false,
      deleteName: '',
      deleting: false,
    }
  },
  computed: {
    pageCount() {
      return Math.ceil(this.suppliers.length / PER_PAGE) || 0
    },
    pagedSuppliers() {
      const start = (this.page - 1) * PER_PAGE
      return this.suppliers.slice(start, start + PER_PAGE)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      try {
        this.suppliers = await getSuppliers()
        if (this.page > this.pageCount && this.pageCount > 0) {
          this.page = this.pageCount
        }
      } catch (error) {
        notifyError(error)
      }
    },
    openCreate() {
      this.isEdit = false
      this.supplierId = null
      this.form = emptyForm()
      this.formOpen = true
    },
    openEdit(supplier) {
      this.isEdit = true
      this.supplierId = supplier.id
      this.form = {
        name: supplier.name || '',
        phone: supplier.phone || '',
        note: supplier.note || '',
      }
      this.formOpen = true
    },
    askDelete(supplier) {
      this.supplierId = supplier.id
      this.deleteName = supplier.name
      this.deleteOpen = true
    },
    async confirmDelete() {
      this.deleting = true
      try {
        await deleteSupplier(this.supplierId)
        notify(this.$t('catalog.suppliers.deleted'), 'warning')
        this.deleteOpen = false
        await this.load()
      } catch (error) {
        notifyError(error, this.$t('catalog.suppliers.deleteFailed'))
      } finally {
        this.deleting = false
      }
    },
    async save() {
      if (!this.form.name) {
        notify(this.$t('catalog.suppliers.enterName'), 'warning')
        return
      }

      this.saving = true
      try {
        const payload = {
          name: this.form.name,
          phone: this.form.phone || null,
          note: this.form.note || null,
        }
        const result = this.isEdit
          ? await updateSupplier(this.supplierId, payload)
          : await createSupplier(payload)

        notify(result.message || this.$t('catalog.common.saved'))
        this.formOpen = false
        await this.load()
      } catch (error) {
        notifyError(error)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.suppliers-page {
  padding: 28px 32px 40px;
  color: #152033;
}

.suppliers-page__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    color: #6b7a90;
    font-size: 14px;
  }
}

.suppliers-page__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border: 0;
  border-radius: 12px;
  background: #0b4f8a;
  color: #fff;
  font-weight: 650;
  cursor: pointer;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    background: #093f6d;
  }
}

.suppliers-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
}

.supplier-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 16px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.35;
  }
}

.supplier-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.supplier-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: #0b4f8a;

  .mdi {
    font-size: 22px;
  }
}

.supplier-card__actions {
  display: flex;
  gap: 6px;
}

.supplier-card__icon-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #d9e2ec;
  border-radius: 10px;
  background: #fff;
  color: #3d4f63;
  display: grid;
  place-items: center;
  cursor: pointer;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    border-color: #0b4f8a;
    color: #0b4f8a;
    background: #eef4fa;
  }

  &--danger {
    color: #b42318;

    &:hover {
      border-color: #f0b4ae;
      background: #fff5f4;
    }
  }
}

.supplier-card__meta {
  margin: 0;
  color: #3d4f63;
  font-size: 13px;
  font-weight: 600;
}

.supplier-card__note {
  margin: 0;
  color: #6b7a90;
  font-size: 12px;
  line-height: 1.4;
}

.suppliers-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 240px;
  padding: 48px;
  text-align: center;
  color: #6b7a90;
  border: 1px dashed #d9e2ec;
  border-radius: 16px;
  background: #fff;

  .mdi {
    font-size: 40px;
    color: #9eb6cc;
  }

  p {
    margin: 0;
    color: #152033;
    font-size: 16px;
    font-weight: 700;
  }

  span {
    font-size: 13px;
  }
}

.suppliers-page__pagination {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

.supplier-modal {
  overflow: hidden;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(18, 32, 51, 0.18);
}

.supplier-modal__header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 20px 0;
}

.supplier-modal__badge {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: #0b4f8a;

  .mdi {
    font-size: 22px;
  }

  &--danger {
    background: #fff1f0;
    color: #b42318;
  }
}

.supplier-modal__header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 750;
  color: #152033;
}

.supplier-modal__header p {
  margin: 4px 0 0;
  color: #6b7a90;
  font-size: 13px;
}

.supplier-modal__close {
  margin-left: auto;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: #f3f6fa;
  color: #6b7a90;
  display: grid;
  place-items: center;
  cursor: pointer;
}

.supplier-modal__body {
  display: grid;
  gap: 14px;
  padding: 20px;
}

.supplier-modal__field {
  display: block;

  > span {
    display: block;
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 700;
    color: #152033;
  }

  input {
    width: 100%;
    padding: 14px 16px;
    border: 1.5px solid #d9e2ec;
    border-radius: 14px;
    background: #fbfdff;
    color: #152033;
    font-size: 15px;
    font-weight: 600;
    outline: none;

    &:focus {
      border-color: #0b4f8a;
      box-shadow: 0 0 0 4px rgba(11, 79, 138, 0.1);
    }
  }
}

.supplier-modal__warning {
  margin: 0;
  padding: 14px 16px;
  border-radius: 14px;
  background: #fff7f6;
  border: 1px solid #f5d5d1;
  color: #5b3a38;
  font-size: 14px;
}

.supplier-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 20px 20px;
}

.supplier-modal__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 120px;
  padding: 12px 16px;
  border: 0;
  border-radius: 12px;
  background: #0b4f8a;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &--ghost {
    background: #f3f6fa;
    color: #3d4f63;
  }

  &--danger {
    background: #b42318;
  }
}

@media (max-width: 720px) {
  .suppliers-page {
    padding: 20px 16px 32px;
  }

  .suppliers-page__header {
    flex-direction: column;
  }

  .supplier-modal__footer {
    flex-direction: column-reverse;

    .supplier-modal__btn {
      width: 100%;
    }
  }
}
</style>

<style lang="scss">
.supplier-dialog-wrap {
  box-shadow: none !important;
  background: transparent !important;
  overflow: visible !important;
}
</style>
